import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { resolveEffectiveTimezone } from './timezone-display'
import permanentDstPlugin from './dayjs-permanent-dst'

// Ensure dayjs plugins are loaded
dayjs.extend(utc)
dayjs.extend(timezone)
// Keeps zones that abolished their clock change reading correctly on a runtime
// whose timezone database still has the old rule — see dayjs-permanent-dst.
dayjs.extend(permanentDstPlugin)

/**
 * Serializes a registration date/time picked in the competition account's
 * timezone into the UTC ISO string the backend stores.
 *
 * The picked `Dayjs` carries a wall-clock (what the user typed in the picker);
 * we reinterpret that wall-clock as being in `accountTimezone` and convert to
 * UTC. When the account has no timezone we use the viewer's own rather than
 * UTC — see `resolveEffectiveTimezone`.
 *
 * Example output: "2026-07-15T15:24:00Z"
 *
 * @param value - the Dayjs picked in the account timezone
 * @param accountTimezone - IANA timezone from the account address (e.g. 'America/New_York')
 * @returns UTC ISO string, or '' when value is empty
 */
export const registrationDateTimeToUtcIso = (
  value: Dayjs | null | undefined,
  accountTimezone?: string
): string => {
  if (!value || !value.isValid()) {
    return ''
  }

  const wallClock = value.format('YYYY-MM-DDTHH:mm:ss')
  const effectiveTimezone = resolveEffectiveTimezone(accountTimezone)
  const zoned = effectiveTimezone
    ? dayjs.tz(wallClock, effectiveTimezone)
    : dayjs.utc(wallClock)

  return zoned.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
}

/**
 * Parses a stored UTC ISO registration date/time into a `Dayjs` expressed in
 * the competition account's timezone, so the picker displays the same
 * wall-clock the user originally selected. When the account has no timezone we
 * use the viewer's own rather than UTC — see `resolveEffectiveTimezone`.
 *
 * @param isoString - stored UTC ISO string (e.g. '2026-07-15T15:24:00Z')
 * @param accountTimezone - IANA timezone from the account address
 * @returns Dayjs in the account timezone, or null when input is empty
 */
export const registrationDateTimeFromUtcIso = (
  isoString: string | null | undefined,
  accountTimezone?: string
): Dayjs | null => {
  if (!isoString) {
    return null
  }

  const asUtc = dayjs.utc(isoString)
  if (!asUtc.isValid()) {
    return null
  }

  const effectiveTimezone = resolveEffectiveTimezone(accountTimezone)
  return effectiveTimezone ? asUtc.tz(effectiveTimezone) : asUtc
}

/**
 * Formats a stored UTC ISO registration date/time for display in the account's
 * timezone, as date + 12-hour time (e.g. 'Apr 1, 2026 5:04 PM'). Falls back to
 * the viewer's timezone when the account has none. Returns '' for empty input.
 *
 * @param isoString - stored UTC ISO string (e.g. '2026-04-02T00:04:00Z')
 * @param accountTimezone - IANA timezone from the account address
 */
export const formatRegistrationDateTime = (
  isoString: string | null | undefined,
  accountTimezone?: string
): string => {
  const zoned = registrationDateTimeFromUtcIso(isoString, accountTimezone)
  return zoned ? zoned.format('MMM D, YYYY h:mm A') : ''
}

/**
 * Formats a registration window — two stored UTC instants — as calendar dates
 * in the account's timezone (e.g. 'Aug 24 – Oct 16, 2026', or
 * 'Sep 25, 2026 – Feb 28, 2027' across a year). A missing end gives the start
 * alone; '' when the start is empty or unparseable.
 *
 * Read through `registrationDateTimeFromUtcIso`, so a late-evening close lands
 * on the day the organizer picked rather than the next UTC day.
 *
 * @param startIso - stored UTC ISO string the window opens at
 * @param endIso - stored UTC ISO string the window closes at
 * @param accountTimezone - IANA timezone from the account address
 */
export const formatRegistrationDateRange = (
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  accountTimezone?: string | null
): string => {
  const { start, end } = formatRegistrationDateRangeParts(
    startIso,
    endIso,
    accountTimezone
  )
  return end ? `${start} – ${end}` : start
}

/**
 * `formatRegistrationDateRange`'s two ends, for a layout that sets them on
 * separate lines: `{ start: 'Aug 24', end: 'Oct 16, 2026' }`. The start carries
 * its year only when the window crosses one. `end` is '' when the window has no
 * end; both are '' when the start is empty or unparseable.
 *
 * @param startIso - stored UTC ISO string the window opens at
 * @param endIso - stored UTC ISO string the window closes at
 * @param accountTimezone - IANA timezone from the account address
 */
export const formatRegistrationDateRangeParts = (
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  accountTimezone?: string | null
): { start: string; end: string } => {
  const start = registrationDateTimeFromUtcIso(
    startIso,
    accountTimezone ?? undefined
  )
  if (!start) {
    return { start: '', end: '' }
  }
  const end = registrationDateTimeFromUtcIso(
    endIso,
    accountTimezone ?? undefined
  )
  if (!end) {
    return { start: start.format('MMM D, YYYY'), end: '' }
  }
  const startFormat = start.year() === end.year() ? 'MMM D' : 'MMM D, YYYY'
  return { start: start.format(startFormat), end: end.format('MMM D, YYYY') }
}

/**
 * A stored registration instant's calendar day in the account's timezone, as
 * month + day (e.g. 'Oct 16'); '' for empty or unparseable input.
 *
 * @param isoString - stored UTC ISO string
 * @param accountTimezone - IANA timezone from the account address
 */
export const formatRegistrationMonthDay = (
  isoString: string | null | undefined,
  accountTimezone?: string | null
): string =>
  registrationDateTimeFromUtcIso(
    isoString,
    accountTimezone ?? undefined
  )?.format('MMM D') ?? ''

/**
 * Whole calendar days from `now` until a stored registration instant, counted
 * on the account timezone's calendar: 0 on the day itself, 1 the day before,
 * negative once the day has passed; null for empty or unparseable input.
 *
 * Calendar days rather than 24-hour blocks, so "Closes in 1 day" means
 * tomorrow — a close at 11:59 PM tomorrow still reads as one day away at 8 AM
 * today.
 *
 * @param isoString - stored UTC ISO string
 * @param accountTimezone - IANA timezone from the account address
 * @param now - the reference moment, for tests; defaults to the current time
 */
export const registrationCalendarDaysUntil = (
  isoString: string | null | undefined,
  accountTimezone?: string | null,
  now: Date = new Date()
): number | null => {
  const target = registrationDateTimeFromUtcIso(
    isoString,
    accountTimezone ?? undefined
  )
  const today = registrationDateTimeFromUtcIso(
    now.toISOString(),
    accountTimezone ?? undefined
  )
  if (!target || !today) {
    return null
  }
  return target.startOf('day').diff(today.startOf('day'), 'day')
}
